import React, { PureComponent } from 'react'
import FunkGoHelmet from './FunkGoHelmet'
import PluginContext from './PluginContext'

import { createTransportContext } from './utils/transportContext'

/* ----------------------------------------- */

const getInitState = (key, props) => {
  // if running on server side single component mode, the pre-fetched data will assign props.data
  if (typeof props[key] !== 'undefined') {
    return props[key]

  // if running on server side route mode, the pre-fetched data will by pass via static context
  } else if (props.staticContext && typeof props.staticContext[key] !== 'undefined') {
    return props.staticContext[key]
  }

  // if running on client side, keep it null
  return null
}

// for lifecycle usage, it can only run after didMount, so include all plugins
// transportContext may include location, history, match props
const getRuntimeInjectProps = (pluginHub, transportContext) => {
  return pluginHub.getInjectProps(transportContext)
}

// for fetchPageData, it may run on server-side, so only include non-client-only plugins
// transportContext may include location, history, match props
const getStaticInjectProps = (pluginHub, transportContext) => {
  const staticInjectProps = pluginHub.getStaticInjectProps(transportContext)

  return {
    ...staticInjectProps,
    ...transportContext,
  }
}

const formatOpenGraphInfo = (pluginHub, transportContext, openGraphInfo = {}) => {
  const appInfo = pluginHub.getAppInfo()
  const fbAppId = appInfo.fbAppId

  const location = transportContext.location

  const $site = pluginHub.getPlugin('site')
  const site = $site.getSiteInfo()

  const image = openGraphInfo.image || appInfo.shareImage

  return {
    fbAppId,
    siteName: site.fullSiteName || appInfo.siteName,
    title: openGraphInfo.title || `${site.fullSiteName}: ${appInfo.siteTitle}`,
    seoTitle: openGraphInfo.seoTitle,
    url: openGraphInfo.url || `https://${appInfo.domain}${location.pathname}`,
    description: openGraphInfo.description || `${site.fullSiteName} ${appInfo.siteDesc}`,
    image: image.src ? image.src : image,
    private: openGraphInfo.private === true,
    noIndex: site.id.indexOf('FLAMINGO') <= -1 || openGraphInfo.noIndex === true,
    jsonLD: openGraphInfo.jsonLD
  }
}

const isFunction = field => typeof field === 'function'

const JourneyWithDeepLinkPages = [
  { name: 'SalesEvent', pageId: 'event' },
  { name: 'Collection', pageId: 'collection' },
  { name: 'Product', pageId: 'product' }
]

let urlOfLastClosedJourney = null

/* ----------------------------------------- */

const createFunkGoPage = (PageComponent, pluginHub, options = {}) => {
  if (!PageComponent) {
    throw new Error('[FunkGoPage] invalid parameter to createFunkGoPage')
  }

  /* ----------------------------------------- */
  const {
    ssr,

    fetchPageData,
    fetchPageDataSSR,
    fetchOpenGraphInfo,
    fetchStaticShellProps,

    LoadingComponent,
    ErrorComponent,

    Shell,
    shellProps,
  } = PageComponent

  /* ----------------------------------------- */

  // defined at PageComponent which means self-defined config,
  // defined at options which means other user call createPage and want to overwrite config
  const pageOptions = {
    ...PageComponent.options,
    ...options
  }

  const displayName = PageComponent.displayName || PageComponent.name

  /* ----------------------------------------- */
  const getFetchingProps = transportContext => getStaticInjectProps(pluginHub, transportContext)
  const getPluginProps = transportContext => getRuntimeInjectProps(pluginHub, transportContext)
  const isFetchPageDataAsync = () => isFunction(fetchPageData)
  const isFetchPageDataSsrAsync = () => isFunction(fetchPageDataSSR)

  /* ----------------------------------------- */
  const ensureFetchingProps = (transportContext, fetchingProps) => {
    if (fetchingProps) {
      return fetchingProps
    } else if (transportContext) {
      return getFetchingProps(transportContext)
    } else {
      throw new Error(`invalid transportContext for ensureFetchProps()`)
    }
  }

  const callFetchPageData = (transportContext, _fetchingProps) => {
    if (isFetchPageDataAsync()) {
      const fetchingProps = ensureFetchingProps(transportContext, _fetchingProps)
      return Promise.resolve(fetchPageData(fetchingProps))
    }
    return Promise.resolve({})
  }

  const callFetchPageDataSSR = (transportContext, _fetchingProps) => {
    if (isFetchPageDataSsrAsync()) {
      const fetchingProps = ensureFetchingProps(transportContext, _fetchingProps)
      return Promise.resolve(fetchPageDataSSR(fetchingProps))
    }

    return callFetchPageData(transportContext, _fetchingProps)
  }

  const callFetchOpenGraphInfo = (transportContext, _fetchingProps, data) => {
    let ogInfoPromise

    if (!isFunction(fetchOpenGraphInfo)) {
      ogInfoPromise = Promise.resolve({})
    } else if (!fetchOpenGraphInfo.length) {
      ogInfoPromise = Promise.resolve(fetchOpenGraphInfo())
    } else {
      const dataFetching = data || fetchPageData(transportContext, _fetchingProps)

      ogInfoPromise = Promise.resolve(dataFetching).then(data => {
        const fetchingProps = ensureFetchingProps(transportContext, _fetchingProps)
        return fetchOpenGraphInfo({ data }, fetchingProps)
      })
    }

    return ogInfoPromise.then(
      ogInfo => formatOpenGraphInfo(pluginHub, transportContext, ogInfo)
    )
  }

  /* ----------------------------------------- */
  const fetchPageInfo = (transportContext, initData, callFromServer = false) => {
    const fetchingProps = ensureFetchingProps(transportContext)
    let dataFetching = initData

    if (!initData) {
      dataFetching = callFromServer
        ? callFetchPageDataSSR(transportContext, fetchingProps)
        : callFetchPageData(transportContext, fetchingProps)
    }

    return Promise.resolve(dataFetching).then(data => (
      callFetchOpenGraphInfo(transportContext, fetchingProps, data).then(ogInfo => ({
        data,
        ogInfo,
      }))
    ))
  }

  const fetchPageInfoSSR = transportContext => fetchPageInfo(transportContext, undefined, true)
  const fetchStaticShellPropsSSR = transportContext => {
    const fetchingProps = ensureFetchingProps(transportContext)
    if (Shell && typeof Shell.fetchStaticShellProps === 'function') {
      return Shell.fetchStaticShellProps(fetchingProps)
    } else if (fetchStaticShellProps) {
      return fetchStaticShellProps(fetchingProps)
    } else {
      return Promise.resolve({})
    }
  }

  /* ----------------------------------------- */

  const WrappedComponent = class FunkGoPage extends PureComponent {
    static ssr = ssr // if disabled, it will skip ssr
    static displayName = displayName

    /* static fetching methods (for server-side usage) */
    /* ----------------------------------------- */

    static fetchPageInfo = fetchPageInfo
    static fetchPageInfoSSR = fetchPageInfoSSR
    static fetchStaticShellPropsSSR = fetchStaticShellPropsSSR
    static getDerivedStateFromError = error => ({ error })

    /* ----------------------------------------- */

    _isMounted = false

    state = {
      isLoading: false,
      data: getInitState('data', this.props),
      error: getInitState('error', this.props),
      ogInfo: getInitState('ogInfo', this.props),
    }

    /* runtime fetching methods (client-side) */
    /* ----------------------------------------- */
    _cachedLocation = null
    _cachedTransportContext = null
    _cachedPluginProps = null

    /* ----------------------------------------- */

    getCurrentTransportContext() {
      // if enable router plugin, or running on server side, page will contains location, history, match automatically
      if (this.props.location) {
        if (!this._cachedTransportContext || this._cachedLocation !== this.props.location) {
          this._cachedTransportContext = createTransportContext({
            location: this.props.location,
            history: this.props.history,
            match: this.props.match,
          })

          this._cachedLocation = this.props.location
        }

        return this._cachedTransportContext
      } else {
        throw new Error('[FunkGoPage] could not found location from context')
      }
    }

    getCurrentPluginProps() {
      const lastCachedTransportContext = this._cachedTransportContext
      const transportContext = this.getCurrentTransportContext()

      // loadable 会缓存一个funkgopage见_renderComponent
      if (this.props.staticContext && this.props.staticContext.pluginHub) {
        return this.props.staticContext.pluginHub.getInjectProps(transportContext)
      }

      if (!this._cachedPluginProps || transportContext !== lastCachedTransportContext) {
        this._cachedPluginProps = getPluginProps(transportContext)
      }
      return this._cachedPluginProps
    }

    getCurrentPageProps() {
      return {
        ...this.getCurrentPluginProps(),
        ...this.props,
        ogInfo: this.state.ogInfo,
        error: this.state.error,
        data: this.state.data
      }
    }

    getCurrentShellProps() {
      const props = isFunction(shellProps)
        ? shellProps(this.getCurrentPluginProps())
        : shellProps

      const shellPropsFromServer = this.props.staticContext ? this.props.staticContext.shellPropsFromServer : (this.state.data ? this.state.data.shellPropsFromServer : {})

      return {
        ...props,
        ...shellPropsFromServer,
        ogInfo: this.state.ogInfo,
      }
    }

    /* ----------------------------------------- */

    handleServiceWorkerUpdate = () => {
      const { $detector, $router } = this.getCurrentPluginProps()
      if ($router && !$detector.isApp()) {
        $router.forceSwitchToReloadPage()
      }
    }

    /* ----------------------------------------- */

    trackRenderError(error, label) {
      const { $track } = this.getCurrentPluginProps()

      if ($track) {
        $track.error({
          isFatal: true,
          category: 'PAGE_RENDER',
          error,
          label
        })
      }
    }

    trackPageView() {
      const { $track, $router } = this.getCurrentPluginProps()
      const location = $router.location

      if ($track) {
        if (pageOptions.skipPv !== true) {
          $track.pv(location)
        } else {
          $track.setPage(location)
        }
      }
    }

    /* ----------------------------------------- */

    refreshPage(initData) {
      this.trackPageView()

      const isNeedToDisplayLogin = !initData && isFetchPageDataAsync()

      if (isNeedToDisplayLogin) {
        this.setState({
          isLoading: true
        })
      }

      const transportContext = this.getCurrentTransportContext()

      fetchPageInfo(transportContext, initData).then(({ data, ogInfo }) => {
        this.setStateIfMounted({
          data,
          ogInfo,
          isLoading: false,
          error: null
        })
      }).catch(error => {
        this.trackRenderError(error)

        this.setStateIfMounted({
          error,
          isLoading: false,
        })
      })
    }

    /* ------------------------------------------------------- */
    setStateIfMounted(state) {
      if (this._isMounted) {
        this.setState(state)
      }
    }

    componentDidMount() {
      this._isMounted = true
      if (window._INIT_DATA) {
        window.globalMetaInfo = window._INIT_DATA.metaInfo
        this.refreshPage(window._INIT_DATA)
        delete window._INIT_DATA
      } else {
        this.refreshPage(this.props.data)
      }

      window.addEventListener('serviceWorkerUpdated', this.handleServiceWorkerUpdate, false)
    }

    componentWillUnmount() {
      this._isMounted = false

      window.removeEventListener('serviceWorkerUpdated', this.handleServiceWorkerUpdate, false)
    }

    componentDidUpdate(prevProps) {
      const currLocation = this.props.location
      const prevLocation = prevProps.location

      // location may be null, if app not enabled router
      if (currLocation && prevLocation
        && currLocation.key !== prevLocation.key　
        && !currLocation.hash && !prevLocation.hash) {

        this.refreshPage()
      }
    }

    componentDidCatch(error, info) {
      this.trackRenderError(error, info.componentStack)
    }

    /* -------------------------------- */

    renderComponent(Component) {
      return (
        <Component {...this.getCurrentPageProps()}></Component>
      )
    }

    renderLifeCycle(name, fallback) {
      const $lifeCycle = pluginHub.getPlugin('lifeCycle')

      if (!$lifeCycle) {
        return null
      }

      const Component = $lifeCycle.getComponent(name) || $lifeCycle.getComponent(fallback)

      if (!Component) {
        return null
      }

      return this.renderComponent(Component)
    }

    /* -------------------------------- */

    renderLoading() {
      if (isFunction(LoadingComponent)) {
        return this.renderComponent(LoadingComponent)
      }
      return this.renderLifeCycle('Loading')
    }

    renderError(error) {
      if (isFunction(ErrorComponent)) {
        return this.renderComponent(ErrorComponent)
      } else if (error.statusCode === 404 || (error.statusCode === 200 && error.errorCode === 404)) {
        return this.renderLifeCycle('NotFound', 'Error')
      }
      return this.renderLifeCycle('Error')
    }

    renderPage() {
      const ogInfo = this.state.ogInfo

      return (
        <>
          {ogInfo && (
            <FunkGoHelmet {...ogInfo}></FunkGoHelmet>
          )}

          {this.renderComponent(PageComponent)}
        </>
      )
    }

    /* -------------------------------- */
    renderElement(element) {
      if (Shell) {
        return (
          <Shell {...this.getCurrentShellProps()}>
            {element}
          </Shell>
        )
      } else {
        return element
      }
    }

    /* -------------------------------- */

    render() {
      let element

      if (this.state.isLoading) {
        element = this.renderLoading()
      } else if (this.state.error) {
        element = this.renderError(this.state.error)
      } else if (this.state.data) {
        element = this.renderPage()
      } else {
        element = this.renderLifeCycle('Initial')
      }

      return (
        <PluginContext.Provider value={this.getCurrentPluginProps()}>
          {this.renderElement(element)}
        </PluginContext.Provider>
      )
    }
  }

  return WrappedComponent
}

export {
  createFunkGoPage,
}
